import * as _ from 'lodash';
import qs from 'qs';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../contexts/ToastContext';
import { window } from '../../globals';
import useToastedMutation from '../../ReactQuery/useToastedMutation';
import { get } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import utils from '../../utils';
import DropdownButton from '../generic/DropdownButton';
import { icons, IconText } from '../generic/Icon';
import { useAddJobToNotificationQueue } from '../JobNotificationHub/Provider';
import { issueDashboardFeature } from '../settings/billing/features/consts';
import type { Task } from '../../types';

import DashboardReportCard from './DashboardReportCard';

const GenerateReportIconText = <IconText icon={icons.addToProject} text="Generate report" />;

/**
 * The pre-CORE-7309 "Global Issue CSV Export" card: a Download / Email dropdown over
 * `GET /api/v2/issues/csv/global` scoped to the org (or the globally selected teams). Rendered when the
 * `teamGroupIssueReport` flag is off, so the classic export is preserved during rollout. Delete this
 * component and its branch in DashboardActionCards when the flag is retired.
 */
export const LegacyGlobalIssueCSVExportCard: VoidFunctionComponent = () => {
  const selectedTeams = useSelector(TeamsModule.selectors.getTeamSelection);
  const addJob = useAddJobToNotificationQueue();
  const addToast = useAddToast();

  // Emailing the report is a background job; the mutation surfaces the outcome as a toast and hands the
  // job to the notification queue on success.
  const emailExport = useToastedMutation<Task | undefined>({
    mutationFn: async () => {
      const response = await get<Task>('/api/v2/issues/csv/global', false, {
        email: true,
        ...(!_.isEmpty(selectedTeams) ? { teamIds: selectedTeams } : {}),
      });
      if (!response.ok) {
        throw new Error('Failed to queue CSV export');
      }
      return response.data;
    },
    successToastMessage: 'Your report is being generated and will be emailed to you.',
    errorToastMessage: 'Failed to generate the report.',
    onSuccess: (task) => {
      if (task?.jobToken) {
        addJob(task.jobToken, task.task);
      }
    },
  });

  // DropdownButton's onSelect passes the selected item's eventKey.
  const generate = (eventKey?: unknown) => {
    // Premium gate — mirrors the old checkAccess paywall.
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal({ focusFeature: issueDashboardFeature });
      return;
    }

    if (eventKey === 'email') {
      emailExport.mutate();
      return;
    }

    // Download: stream the zip in a new tab.
    const params = _.isEmpty(selectedTeams) ? '' : `?${qs.stringify({ teamIds: selectedTeams })}`;
    utils.openInNewTab(`/api/v2/issues/csv/global${params}`);
    addToast({ type: 'success', content: 'Your report will begin downloading shortly.' });
  };

  return (
    <div>
      <DashboardReportCard
        title="Global Issue CSV Export"
        description="Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues."
      >
        <DropdownButton
          id="GlobalCSVExportV2Options"
          title={GenerateReportIconText}
          size="large"
          onSelect={generate}
          disabled={emailExport.isPending}
        >
          <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
          <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
        </DropdownButton>
      </DashboardReportCard>
    </div>
  );
};
